import React, { useState } from 'react';
import { MailIcon, MapPinIcon, CheckIcon } from 'lucide-react';
import { FadeUp } from '../motion/FadeUp';
type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
};
const EMPTY: FormState = {
  name: '',
  email: '',
  subject: '',
  message: ''
};
const FORM_ENDPOINT = 'https://formsubmit.co/ajax/hello@haerumbah.com';
export function Contact() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const update = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(f => ({
    ...f,
    [key]: e.target.value
  }));
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          _subject: `Haerumbah website: ${form.subject}`,
          _template: 'table',
          _captcha: 'false'
        })
      });
      if (!response.ok) throw new Error('Contact request failed.');
      setStatus('sent');
      setForm(EMPTY);
    } catch {
      setStatus('error');
    }
  };
  const inputClass = 'w-full rounded-xl border border-black/[0.12] bg-white px-4 py-3 text-[15px] text-ink placeholder-ink/35 outline-none transition-colors focus:border-vermillion focus:ring-2 focus:ring-vermillion/15';
  return <section id="contact" className="w-full bg-white px-6 py-28 md:py-36 lg:px-8" data-id="element-76" data-mp="3dmx8rktq1bqe">
      <div className="mx-auto max-w-6xl" data-id="element-77" data-mp="3dorvn4ojzdx4">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20" data-id="element-78" data-mp="38ond79434949">
          {/* Left — info */}
          <div data-id="element-79" data-mp="3frtwe032itdo">
            <FadeUp data-id="element-80" data-mp="3fq0roqc0mj8b">
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-vermillion" data-id="element-81" data-mp="3fqslpwpys8wh">
                Contact
              </p>
            </FadeUp>
            <FadeUp delay={0.08} data-id="element-82" data-mp="3poore904ubn7">
              <h2 className="mt-5 text-4xl font-semibold leading-[1.1] tracking-tightest text-ink md:text-5xl" data-id="element-83" data-mp="3pprioi1dag3n">
                Let&rsquo;s build something meaningful together.
              </h2>
            </FadeUp>
            <FadeUp delay={0.14} data-id="element-84" data-mp="2wyg98h5ljr8m">
              <p className="mt-6 max-w-md text-lg leading-relaxed text-ink/60" data-id="element-85" data-mp="31zdyfdp0xljm">
                Whether you&rsquo;re a customer, business, investor, media partner, or
                simply interested in what we&rsquo;re building, we&rsquo;d love to hear
                from you.
              </p>
            </FadeUp>

            <FadeUp delay={0.2} data-id="element-86" data-mp="2eaupqx22b6r0">
              <div className="mt-10 space-y-6" data-id="element-87" data-mp="2e9t6i5aw7dy7">
                <div className="flex items-start gap-4" data-id="element-88" data-mp="2j8miftaj6ww9">
                  <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-vermillion" data-id="element-89" data-mp="2j71pg83v7m0i">
                    <MailIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-90" data-mp="wc6kgkisdq8u" />
                  </span>
                  <div data-id="element-91" data-mp="rctwtxk3cf57">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-ink/40" data-id="element-92" data-mp="rc21mnueojfg">
                      Business Email
                    </p>
                    <a href="mailto:hello@haerumbah.com" className="text-lg font-medium text-ink transition-colors hover:text-vermillion" data-id="element-93" data-mp="hexdiih02dw0">
                      hello@haerumbah.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-94" data-mp="3oaoji11k2d4">
                  <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-vermillion" data-id="element-95" data-mp="3lo9kxbtmkkv">
                    <MapPinIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-96" data-mp="3pupfcx6rgfgg" />
                  </span>
                  <div data-id="element-97" data-mp="1trkq9o74ib9l">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-ink/40" data-id="element-98" data-mp="1tt5kfcpizs6x">
                      Location
                    </p>
                    <p className="text-lg font-medium text-ink" data-id="element-99" data-mp="1k219qp6vuthw">India</p>
                  </div>
                </div>

                <a href="https://maps.app.goo.gl/j95WVDHbc3Tb83uF8" target="_blank" rel="noopener noreferrer" className="group flex h-44 flex-col items-center justify-center rounded-2xl border border-black/[0.08] bg-[#fafafa] text-center transition-colors hover:border-[#2f5f1e]/30 hover:bg-[#f7f9f4]" aria-label="Open Haerumbah's location in Google Maps" data-id="element-100" data-mp="1p0m5abb6wp00">
                  <MapPinIcon className="h-6 w-6 text-[#2f5f1e]" strokeWidth={1.5} data-id="element-101" data-mp="16435wdq6ufgn" />
                  <span className="mt-3 text-sm font-medium text-ink/65 transition-colors group-hover:text-[#2f5f1e]" data-id="element-102" data-mp="2vhzo98kty7qi">
                    View our location on Google Maps
                  </span>
                </a>
              </div>
            </FadeUp>
          </div>

          {/* Right — form */}
          <FadeUp delay={0.16} data-id="element-103" data-mp="jrrro7p3p2uu">
            <form onSubmit={handleSubmit} className="rounded-2xl border border-black/[0.08] bg-white p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_48px_-30px_rgba(0,0,0,0.2)] md:p-10" data-id="element-104" data-mp="yih75m9lh44i">
              <div className="space-y-5" data-id="element-105" data-mp="17ie0w2auct6f">
                <div data-id="element-106" data-mp="17li878jls51b">
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-ink/70" data-id="element-107" data-mp="13i3tqmbbbwpz">
                    Full Name
                  </label>
                  <input id="name" type="text" required value={form.name} onChange={update('name')} placeholder="Your name" className={inputClass} data-id="element-108" data-mp="1hh37v0178t7w" />
                </div>
                <div data-id="element-109" data-mp="3ec81wnw05u2l">
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-ink/70" data-id="element-110" data-mp="1i70d235euidt">
                    Email Address
                  </label>
                  <input id="email" type="email" required value={form.email} onChange={update('email')} placeholder="you@example.com" className={inputClass} data-id="element-111" data-mp="1n63frgxx4cim" />
                </div>
                <div data-id="element-112" data-mp="264fstdrya6jh">
                  <label htmlFor="subject" className="mb-2 block text-sm font-medium text-ink/70" data-id="element-113" data-mp="263wb9y67pk5z">
                    Subject
                  </label>
                  <input id="subject" type="text" required value={form.subject} onChange={update('subject')} placeholder="How can we help?" className={inputClass} data-id="element-114" data-mp="2axs2mhrz08s6" />
                </div>
                <div data-id="element-115" data-mp="2yzdsfm8ab8wi">
                  <label htmlFor="message" className="mb-2 block text-sm font-medium text-ink/70" data-id="element-116" data-mp="2z0ozpw0xuysd">
                    Message
                  </label>
                  <textarea id="message" required rows={5} value={form.message} onChange={update('message')} placeholder="Tell us a little more…" className={`${inputClass} resize-none`} data-id="element-117" data-mp="2p0o9dq4njieo" />
                </div>
              </div>

              <button type="submit" disabled={status === 'sending'} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-vermillion px-6 py-3.5 text-[15px] font-medium text-white transition-colors hover:bg-vermillion-dark disabled:opacity-60" data-id="element-118" data-mp="37zsh8vkgj64r">
                {status === 'sent' ? <>
                    <CheckIcon className="h-4 w-4" data-id="element-119" data-mp="j80xz8xavjll" /> Message sent
                  </> : status === 'sending' ? 'Sending…' : 'Send Message'}
              </button>
              {status === 'error' && <p className="mt-3 text-center text-sm text-vermillion" role="alert" data-id="element-120" data-mp="e8xv9v4slpgs">
                  We couldn&rsquo;t send your message. Please try again or email us directly.
                </p>}
            </form>
          </FadeUp>
        </div>
      </div>
    </section>;
}